import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";
import { Link } from "react-router-dom";
import { useModal } from "@/hooks/useModal";

const pillars = [
  {
    title: "Cybersecurity\nConsulting & Auditing",
    icon: "ri-shield-keyhole-line",
    link: "/services/cybersecurity",
    items: [
      "ISO 27001 / NIST gap analysis",
      "Penetration testing & social engineering",
      "Cybersecurity posture audit & IR planning",
      "24/7 threat monitoring (SOC readiness)",
    ],
  },
  {
    title: "Software Development\n& Engineering",
    icon: "ri-code-box-line",
    link: "/services/software",
    items: [
      "Custom SaaS & business platforms",
      "Cloud-native (AWS, Azure) & API-first",
      "Legacy modernisation & robust architecture",
      "Full-stack engineering: React, .NET, Python, Go",
    ],
  },
  {
    title: "Strategic IT\nConsulting & Audit",
    icon: "ri-customer-service-line",
    link: "/services/consulting",
    items: [
      "IT governance & compliance review",
      "Cloud cost optimisation & architecture audit",
      "Vendor selection & contract negotiation",
      "Disaster recovery & business continuity",
    ],
  },
];

export default function Services() {
  const { openQuote } = useModal();

  return (
    <PageShell>
      <section className="relative w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
              <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                Services
              </span>
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-4">
                IT Services Built for Enterprise
              </h1>
              <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">
                Three pillars of expertise that help organisations scale securely and efficiently.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-5xl mx-auto">
            {pillars.map((p, i) => (
              <ScrollReveal key={p.title} delay={i + 1}>
                <Link
                  to={p.link}
                  className="group block p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border hover:border-brand-200 dark:hover:border-brand-500/30 transition-all duration-300 h-full cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${p.icon} text-xl text-brand-500`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 whitespace-pre-line">
                    {p.title}
                  </h3>
                  <ul className="space-y-3 mb-5">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                        <i className="ri-check-line text-brand-500 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-500 group-hover:text-brand-600 transition-colors">
                    Learn more <i className="ri-arrow-right-line text-xs" />
                  </span>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-white dark:bg-dark-bg">
        <div className="px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center max-w-5xl mx-auto">
            <ScrollReveal>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20sleek%20modern%20healthcare%20technology%20visualization%20showing%20a%20digital%20hospital%20interface%20with%20holographic%20patient%20data%20charts%20and%20medical%20analytics%20dashboards%20displayed%20on%20floating%20translucent%20screens%20with%20warm%20ambient%20lighting%20and%20a%20clean%20minimal%20enterprise%20software%20aesthetic%20with%20soft%20teal%20and%20white%20tones%20against%20a%20dark%20background%20with%20no%20people%20visible&width=600&height=400&seq=healthcarev3&orientation=landscape"
                  alt="Healthcare expertise"
                  className="w-full h-[260px] md:h-[360px] object-cover object-top rounded-2xl"
                  loading="lazy"
                  width={600}
                  height={400}
                />
              </div>
            </ScrollReveal>
            <div>
              <ScrollReveal>
                <span className="inline-block px-3 py-1 rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold uppercase tracking-wider mb-4">
                  Healthcare
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  Our Healthcare Expertise
                </h2>
                <p className="text-sm md:text-base text-gray-500 dark:text-gray-400 leading-relaxed mb-6">
                  AHIS was born from real healthcare needs we encountered during enterprise IT engagements. We saw fragmented systems, paper records, and missed opportunities - so we built a solution.
                </p>
                <Link
                  to="/ahis"
                  className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  View AHIS <i className="ri-arrow-right-line text-xs" />
                </Link>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full py-16 md:py-24 bg-brand-500 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4">
            Need a Custom Engagement?
          </h2>
          <p className="text-sm md:text-base text-brand-100 max-w-lg mx-auto mb-8">
            We tailor our services to your organisation. Tell us what you need.
          </p>
          <button
            onClick={openQuote}
            className="px-8 py-3.5 rounded-full bg-white text-brand-600 text-sm font-semibold hover:bg-brand-50 transition-colors shadow-lg whitespace-nowrap cursor-pointer"
          >
            Get a Quote
          </button>
        </div>
      </section>
    </PageShell>
  );
}